import { FlightSummary } from '@app/models';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { LoadingService } from '../loading.service';
import { catchError, lastValueFrom, map, Observable, of, retry, throwError } from 'rxjs';
import { Reservation } from '@app/models/micro/reservation.model';
import { TypeLoading, RedeemMethod } from '@app/enums';
import { AncillariesMapper } from '@app/mappers/micro/acillaries.mapper';
import { Injectable } from '@angular/core';
import {
  AcillaryResponseInt,
  AncillariesResponseInt,
  BaggageAndServicesInt,
  UpgradeResponseInt,
} from '@app/interfaces/micro/ancillaries.interface';
import {
  UpgradeSegmentOption,
  UpgradeOfferData,
  AncillariesV2Response,
} from '@app/interfaces/micro/upgrade.interface';
import { Passenger } from '@app/models/micro';
import { AcillariesMapper } from '@app/mappers/acillaries.mapper';
import { SeatMapsMapper } from '@app/mappers';
import { ErrorAdapter } from '@models/adapter/error.adapter';

@Injectable({
  providedIn: 'root',
})
export class ApiAncillaries {
  private readonly FLOW = 'CHECKIN';
  /** CVT20-14701: reintentos maximos del rollback del upgrade cuando el pago falla. */
  private readonly RETRIES_REMOVE_UPGRADE = 3;
  /** BFF de ancillaries (sin cifrar por ahora). */
  urlAncillariesBFF = `${environment.urlApiAncillariesBFF}${environment.versionAncillariesBFF}/kiosk/ancillaries`;
  /** Micro de ancillaries: PUT /ac/ancillaries/v2/{pnr} no entra al BFF. */
  urlAncillaries = `${environment.urlAncillaries}`;
  urlCheckin: string = `${environment.urlApiCheckin}`;
  constructor(private _loadingService: LoadingService, private _http: HttpClient) {}

  /**
   * Manejo de errores HTTP para las peticiones.
   * @param {HttpErrorResponse} error - Respuesta de error HTTP
   * @returns {Observable<never>} Observable con error procesado
   */

  handlerError(error: HttpErrorResponse, loading = TypeLoading.FOOTER_LOADING) {
    this._loadingService.hideLoading(loading);
    const adapt = ErrorAdapter.adapt(error.error);
    return throwError(adapt);
  }

  getMaps(reservation: Reservation) {
    const body = AncillariesMapper.seatMapRequest(reservation);
    const headers = new HttpHeaders({
      channel: environment.channel,
      flow: this.FLOW,
      store: 'mx',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'x-transactionId': reservation.flightSummary.cartId,
      reservationCode: reservation.pnr,
    });
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);
    return this._http.post(`${this.urlAncillariesBFF}/seatmap-offer-seats`, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
        return SeatMapsMapper.getSeatMaps(res as any);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
    );
  }

  getAncillaries(reservation: Reservation) {
    // CI-21988: `flightSummary` se leía al construir los headers, así que un
    // flightSummary ausente reventaba antes de emitir la petición. Además
    // HttpHeaders lanza si recibe undefined como valor.
    const flightSummary = reservation?.flightSummary;
    const headers = new HttpHeaders({
      // reservationCode: reservation.pnr,
      channel: environment.channel,
      'x-transactionId': flightSummary?.cartId ?? '',
      'Content-Type': 'application/json',
      flow: this.FLOW,
      store: 'mx',
    });
    const body = AncillariesMapper.getAncillariesRequest(reservation);

    this._loadingService.showLoading(TypeLoading.COMPONENT_LOADING);
    return this._http
      .post<AncillariesResponseInt>(`${this.urlAncillariesBFF}/ancillaries-offer-baggage`, body, {
        headers,
      })
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.COMPONENT_LOADING);

          return AncillariesMapper.getAncillariesResponse(res, flightSummary?.legCode ?? '');
        }),
        catchError((error) => {
          // CI-18515: Log del error
          console.error(' Error en /kiosk/ancillaries:', {
            status: error?.status,
            statusText: error?.statusText,
            error: error?.error,
            message: error?.message,
          });
          return this.handlerError(error, TypeLoading.COMPONENT_LOADING);
        })
      );
  }

  addAncillaries(reservation: Reservation): Observable<any> {
    const headers = this.getHeaders(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);

    const body = AcillariesMapper.getAncillariesRequest(reservation);
    return this._http
      .put(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, { headers })
      .pipe(
        map((response) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return AncillariesMapper.mapResponse(response);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  redeemAncillaries(
    reservation: Reservation,
    passenger: Passenger,
    ancillary: any,
    type: RedeemMethod
  ) {
    const headers = new HttpHeaders({
      channel: environment.channel,
      store: 'mx',
      'x-transactionId': reservation.flightSummary.cartId,
      flow: this.FLOW,
    });
    const body = AncillariesMapper.redeemAncillariesRequest(
      reservation,
      passenger,
      ancillary,
      type
    );
    this._loadingService.showLoading(TypeLoading.COMPONENT_LOADING);
    return this._http
      .put(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, { headers })
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.COMPONENT_LOADING);
          return AncillariesMapper.mapResponse(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.COMPONENT_LOADING))
      );
  }

  removeAncillaries(reservation: Reservation): Observable<any> {
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);
    const body = AncillariesMapper.removeAncillariesRequest(reservation);

    // Validar si el listado de ancillaries está vacío
    const hasAncillaries = body.travelerInfo?.some(
      (traveler) => traveler.ancillaries && traveler.ancillaries.length > 0
    );

    // Si no hay ancillaries, no ejecutar la llamada
    if (!hasAncillaries) {
      this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
      return of(null);
    }

    const headers = new HttpHeaders({
      channel: environment.channel,
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      flow: this.FLOW,
      store: 'mx',
    });
    return this._http
      .put<any>(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, {
        headers,
      })
      .pipe(
        map((response) => {
          this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
          return AncillariesMapper.mapRemoveAncillariesResponse(response);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  setSeat(reservation: Reservation, currentPassenger: Passenger, seatSelected, seatOld) {
    const headers = new HttpHeaders({
      reservationCode: reservation.pnr,
      channel: environment.channel,
      flow: this.FLOW,
      store: 'mx',
      'Content-Type': 'application/json',
      'x-transactionId': reservation.flightSummary.cartId,
    });

    const body = AncillariesMapper.setSeatRequest(
      reservation,
      currentPassenger,
      seatSelected,
      seatOld,
      false
    );
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);

    return this._http.post(`${this.urlAncillariesBFF}/seat-seating`, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
        const resp = AncillariesMapper.respSeat(res);
        AncillariesMapper.validateSeatAssigned(resp, seatSelected);
        return resp;
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
    );
  }

  removeSeat(reservation: Reservation) {
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);
    const headers = new HttpHeaders({
      reservationCode: reservation.pnr,
      channel: 'kiosk',
      flow: 'myb',
      store: 'mx',
      'x-transactionId': reservation.flightSummary.cartId,
    });

    const body = AncillariesMapper.removeAllSeatsRequest(reservation);
    return this._http.post(`${this.urlAncillariesBFF}/seat-seating`, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
        return AncillariesMapper.respSeat(res);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
    );
  }

  getUpgradeOffers(reservation: Reservation) {
    const headers = this.getHeaders(reservation);

    const body = AncillariesMapper.upgradeRequest(reservation);
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);

    return this._http
      .post<UpgradeResponseInt>(`${this.urlAncillariesBFF}/ancillaries-offer-upgrade`, body, {
        headers,
      })
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
          return AncillariesMapper.upgradeResponse(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  getUpgradeOffersFull(reservation: Reservation) {
    const headers = this.getHeaders(reservation);
    const body = AncillariesMapper.upgradeRequest(reservation);
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);

    return this._http
      .post<UpgradeResponseInt>(`${this.urlAncillariesBFF}/ancillaries-offer-upgrade`, body, {
        headers,
      })
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
          return AncillariesMapper.upgradeResponseFull(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  addUpgradeAncillary(
    reservation: Reservation,
    selectedSegments: UpgradeSegmentOption[],
    upgradeOffers: Map<string, UpgradeOfferData>
  ): Observable<AncillariesV2Response> {
    const headers = new HttpHeaders({
      channel: 'kiosk',
      store: 'mx',
      flow: 'CHECKIN',
      'x-transactionId': reservation.flightSummary.cartId,
    });

    const body = AncillariesMapper.addUpgradeAncillaryRequest(
      reservation,
      selectedSegments,
      upgradeOffers
    );

    // CI-23205 (Req 3.7): no mostrar el loader de pantalla completa durante el pago del upgrade; se ve la pantalla de Confirmación de pago.

    return this._http
      .put(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, { headers })
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
          return AncillariesMapper.mapResponse(res);
        }),
        catchError((error) => {
          return this.handlerError(error, TypeLoading.FULL_LOADING);
        })
      );
  }

  removeUpgradeAncillary(
    reservation: Reservation,
    ancillariesResponse: AncillariesV2Response,
    selectedSegments: UpgradeSegmentOption[]
  ): Observable<AncillariesV2Response> {
    const body = AncillariesMapper.removeUpgradeAncillaryRequest(
      reservation,
      ancillariesResponse,
      selectedSegments
    );

    const hasAncillaries = body.travelerInfo?.some(
      (traveler) => traveler.ancillaries && traveler.ancillaries.length > 0
    );
    if (!hasAncillaries) {
      return of(ancillariesResponse);
    }

    const headers = new HttpHeaders({
      channel: 'kiosk',
      store: 'mx',
      flow: 'CHECKIN',
      'x-transactionId': reservation.flightSummary.cartId,
    });

    return this._http
      .put(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, { headers })
      .pipe(
        retry(this.RETRIES_REMOVE_UPGRADE),
        map((res) => AncillariesMapper.mapResponse(res))
      );
  }

  getAcillariesFare(reservation: Reservation) {
    const body = AncillariesMapper.buildBenefitBySegmentBody(reservation);
    const headers = this.getBenefitsHeaders(reservation);

    return this._http
      .post<any>(`${this.urlCheckin}/tc/benefits/benefitBySegment`, body, {
        headers,
      })
      .pipe(
        map((res) => {
          // El nuevo endpoint envuelve la respuesta en benefitBySegment[0].segments[0].benefits
          const benefits: BaggageAndServicesInt =
            res?.benefitBySegment?.[0]?.segments?.[0]?.benefits ?? res;
          return AncillariesMapper.mapAncillariesFare(benefits, reservation);
        }),
        catchError((error) => this.handlerError(error))
      );
  }

  getHeaders(reservation: Reservation, flow = this.FLOW) {
    let headers = new HttpHeaders({
      channel: environment.channel,
      flow: flow,
      cacheEnabled: 'false',
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      store: 'mx',
      reservationCode: reservation.pnr,
    });

    return headers;
  }

  private getBenefitsHeaders(reservation: Reservation): HttpHeaders {
    return new HttpHeaders({
      channel: environment.channel,
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      'User-Agent': 'Aeromexico/1.0',
    });
  }
}
